/*!
 * OVB 代發退水（v2.45.0）
 *
 * OVB（718000）請我們代發退水給他們的玩家與代理。流程（Brad 2026-09-25）：
 *   OVB 通知名單與代理金額好了 → 這頁選週期 → 「讀取 OVB 名單並計算」（預覽，不寫）
 *   → 看過按「儲存」 → 發錢時逐列勾「已發放」 → 補發 / 調整記在實際發放那週
 *
 * 🚫 **只算與顯示**：不接 pipeline、不接營收 / 交收 / Excel。會計看週合計自己扣。
 *
 * 三個 tab：
 *   本週發放   單週的計算、預覽、勾選已發放、補發 / 調整
 *   發放紀錄   人 × 週 的矩陣（取代舊的 Google Sheet），⚠️ 標「可能有問題」的格子
 *   級距設定   級距版本（帶生效週、只增不改）
 *
 * 樣式一律走 constant/adminTheme；loading 照 TinyLoading.js 檔頭的四情境規則。
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Row, Col, Button, Table, Alert, Input, FormGroup, Label,
  Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import axios from "axios";

import TinyPageHeader from "components/tiny/TinyPageHeader";
import TinyStatStrip from "components/tiny/TinyStatStrip";
import TinyPageTabs from "components/tiny/TinyPageTabs";
import TinyWeekSelector from "components/tiny/TinyWeekSelector";
import TinyTooltip from "components/tiny/TinyTooltip";
import TinyLoading, { TinyBusy, TinyLoadingOverlay } from "components/tiny/TinyLoading";
import {
  COLORS, inputStyle, labelStyle, thStyle, tdStyle, softBadge, mutedBadge,
  modalHeaderStyle, modalBodyStyle, modalFooterStyle, cancelBtnStyle, saveBtnStyle,
} from "constant/adminTheme";

const API = "/api/tiny/ovb-rakeback";

// ==================== 小工具 ====================

const fmt = (v) =>
  Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtTime = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString("zh-TW", { hour12: false });
};

// 週期鍵是定寬 YYYYMMDD-YYYYMMDD。日期運算一律用 UTC，避免時區把日子推一天。
const addDays = (ymd, n) => {
  const d = new Date(Date.UTC(+ymd.slice(0, 4), +ymd.slice(4, 6) - 1, +ymd.slice(6, 8)));
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10).replace(/-/g, "");
};
const weekAfter = (w) => {
  const s = addDays(w.slice(9), 1);
  return `${s}-${addDays(s, 6)}`;
};
const shortWeek = (w) => `${w.slice(4, 6)}/${w.slice(6, 8)}–${w.slice(13, 15)}/${w.slice(15, 17)}`;

const apiError = (e) => e?.response?.data?.msg || e?.message || "發生錯誤";

/** 一週紀錄的所有列（玩家 / 代理 / 補發），帶 kind 與 key，勾選已發放用。 */
const rowsOf = (rec) => [
  ...((rec && rec.players) || []).map((r) => ({ ...r, kind: "player", key: r.member_id })),
  ...((rec && rec.agents) || []).map((r) => ({ ...r, kind: "agent", key: r.agent_id })),
  ...((rec && rec.adjustments) || []).map((r) => ({ ...r, kind: "adjustment", key: r.adj_id })),
];

const flagMap = (flags) => {
  const m = {};
  (flags || []).forEach((f) => { (m[f.member_id] = m[f.member_id] || []).push(f.text); });
  return m;
};

const primaryBtn = (disabled) => ({
  ...saveBtnStyle(disabled),
  padding: "9px 22px",
  borderRadius: 8,
  whiteSpace: "nowrap",
  minWidth: 120,
});

const secondaryBtn = {
  background: "rgba(102,126,234,0.15)",
  border: "1px solid rgba(102,126,234,0.4)",
  color: COLORS.accent,
  padding: "8px 18px",
  borderRadius: 8,
  fontWeight: 600,
  whiteSpace: "nowrap",
};

const sectionStyle = {
  background: COLORS.cardBg,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  padding: "14px 16px",
  marginBottom: 14,
};

const sectionTitle = (icon, text, extra = null) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
    <div style={{ color: COLORS.textPrimary, fontWeight: 600, fontSize: 14 }}>
      <i className={`fa ${icon}`} style={{ color: COLORS.info, marginRight: 8 }} />
      {text}
    </div>
    {extra}
  </div>
);

// ==================== 共用小元件 ====================

/** ⚠️ 可能有問題。滑過看說明。 */
const FlagIcon = ({ texts }) =>
  texts && texts.length ? (
    <TinyTooltip content={<div>{texts.map((t, i) => <div key={i} style={{ marginBottom: 4 }}>{t}</div>)}</div>}>
      <i className="fa fa-exclamation-triangle" style={{ color: COLORS.warning, marginLeft: 6 }} />
    </TinyTooltip>
  ) : null;

/** 已發放勾選框。深色主題下原生 checkbox 很難看，自己畫一個。 */
const PaidBox = ({ row, busy, onToggle, readOnly }) => {
  const on = !!row.paid;
  const box = (
    <button
      type="button"
      disabled={busy || readOnly}
      onClick={() => onToggle(row)}
      aria-pressed={on}
      style={{
        width: 22, height: 22, borderRadius: 5, padding: 0, lineHeight: "20px",
        cursor: busy || readOnly ? "default" : "pointer",
        border: `1px solid ${on ? COLORS.success : COLORS.borderInput}`,
        background: on ? "rgba(45,206,137,0.25)" : COLORS.inputBg,
        color: COLORS.success, fontSize: 12,
      }}
    >
      {busy ? <TinyBusy light /> : on ? <i className="fa fa-check" /> : null}
    </button>
  );
  if (!on) return box;
  return (
    <TinyTooltip content={`${row.paid_by || ""}${row.paid_at ? ` · ${fmtTime(row.paid_at)}` : ""}`}>
      {box}
    </TinyTooltip>
  );
};

// ==================== 單週的三張表 ====================

function WeekTables({ rec, flags, busyKey, onToggle, onDeleteAdj, readOnly, preview }) {
  const fm = flagMap(flags);
  const players = rec.players || [];
  const agents = rec.agents || [];
  const adjustments = rec.adjustments || [];
  const showPaid = !preview;

  const paidCell = (row) =>
    showPaid ? (
      <td style={{ ...tdStyle, width: 44, textAlign: "center" }}>
        {Number(row.amount) !== 0 && (
          <PaidBox row={row} busy={busyKey === `${row.kind}:${row.key}`} onToggle={onToggle} readOnly={readOnly} />
        )}
      </td>
    ) : null;

  return (
    <>
      <div style={sectionStyle}>
        {sectionTitle("fa-users", `玩家（${players.filter((p) => p.amount).length} 位有退水）`)}
        <div style={{ overflowX: "auto" }}>
          <Table size="sm" style={{ color: COLORS.textSecondary, marginBottom: 0, minWidth: 760 }}>
            <thead>
              <tr>
                {showPaid && <th style={{ ...thStyle, textAlign: "center" }}>已發放</th>}
                <th style={thStyle}>遊戲 ID</th>
                <th style={thStyle}>遊戲暱稱</th>
                <th style={thStyle}>OVB 提供名稱</th>
                <th style={{ ...thStyle, textAlign: "right" }}>總水量</th>
                <th style={{ ...thStyle, textAlign: "right" }}>趴數</th>
                <th style={{ ...thStyle, textAlign: "right" }}>金額</th>
              </tr>
            </thead>
            <tbody>
              {players.length === 0 && (
                <tr><td colSpan={7} style={{ ...tdStyle, textAlign: "center", color: COLORS.textMuted }}>
                  名單上的玩家這週都沒有打
                </td></tr>
              )}
              {players.map((p) => {
                const row = { ...p, kind: "player", key: p.member_id };
                const zero = !Number(p.amount);
                // 歷史匯入的列：金額是當時實際發的，calc_amount 是照級距應發 —— 不同就標出來
                const off = p.calc_amount != null && Math.abs(p.calc_amount - p.amount) >= 0.02;  // 1 分錢的差不算（Brad：小數差不用管）
                const amountEl = (
                  <span style={off ? { color: COLORS.warning, borderBottom: `1px dashed ${COLORS.warning}` } : null}>
                    {fmt(p.amount)}
                  </span>
                );
                return (
                  <tr key={p.member_id} style={zero ? { opacity: 0.5 } : null}>
                    {paidCell(row)}
                    <td style={{ ...tdStyle, fontFamily: "monospace" }}>
                      {p.member_id}<FlagIcon texts={fm[p.member_id]} />
                    </td>
                    <td style={tdStyle}>{p.nickname || "—"}</td>
                    <td style={{ ...tdStyle, color: COLORS.textPrimary }}>{p.name || "—"}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>{p.rake == null ? "—" : fmt(p.rake)}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>
                      {p.rate_pct ? `${p.rate_pct}%` : p.rate_pct === 0 ? "未達門檻" : "—"}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600, color: COLORS.textPrimary }}>
                      {off ? (
                        <TinyTooltip content={`照級距應為 ${fmt(p.calc_amount)}（差 ${fmt(p.calc_amount - p.amount)}）`}>
                          {amountEl}
                        </TinyTooltip>
                      ) : amountEl}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      </div>

      <div style={sectionStyle}>
        {sectionTitle("fa-user-secret", `代理（${agents.length} 位）`)}
        <div style={{ overflowX: "auto" }}>
          <Table size="sm" style={{ color: COLORS.textSecondary, marginBottom: 0, minWidth: 640 }}>
            <thead>
              <tr>
                {showPaid && <th style={{ ...thStyle, textAlign: "center" }}>已發放</th>}
                <th style={thStyle}>遊戲 ID</th>
                <th style={thStyle}>遊戲暱稱</th>
                <th style={thStyle}>OVB 提供名稱</th>
                <th style={{ ...thStyle, textAlign: "right" }}>OVB 填寫</th>
                <th style={{ ...thStyle, textAlign: "right" }}>金額</th>
              </tr>
            </thead>
            <tbody>
              {agents.length === 0 && (
                <tr><td colSpan={6} style={{ ...tdStyle, textAlign: "center", color: COLORS.textMuted }}>
                  OVB 這週沒有列代理
                </td></tr>
              )}
              {agents.map((a) => {
                const row = { ...a, kind: "agent", key: a.agent_id };
                return (
                  <tr key={a.agent_id}>
                    {paidCell(row)}
                    <td style={{ ...tdStyle, fontFamily: "monospace" }}>
                      {a.agent_id}<FlagIcon texts={fm[a.agent_id]} />
                    </td>
                    <td style={tdStyle}>{a.nickname || "—"}</td>
                    <td style={{ ...tdStyle, color: COLORS.textPrimary }}>{a.name || "—"}</td>
                    <td style={{ ...tdStyle, textAlign: "right", color: COLORS.textMuted }}>
                      {a.raw_amount == null ? "—" : a.raw_amount}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600, color: COLORS.textPrimary }}>
                      {fmt(a.amount)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      </div>

      {(adjustments.length > 0 || !preview) && (
        <div style={sectionStyle}>
          {sectionTitle("fa-square-plus", `補發／調整（${adjustments.length} 筆）`)}
          <div style={{ overflowX: "auto" }}>
            <Table size="sm" style={{ color: COLORS.textSecondary, marginBottom: 0, minWidth: 760 }}>
              <thead>
                <tr>
                  {showPaid && <th style={{ ...thStyle, textAlign: "center" }}>已發放</th>}
                  <th style={thStyle}>遊戲 ID</th>
                  <th style={thStyle}>名稱</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>金額</th>
                  <th style={thStyle}>原因</th>
                  <th style={thStyle}>原本週期</th>
                  <th style={thStyle}>新增者</th>
                  {showPaid && <th style={thStyle} />}
                </tr>
              </thead>
              <tbody>
                {adjustments.length === 0 && (
                  <tr><td colSpan={8} style={{ ...tdStyle, textAlign: "center", color: COLORS.textMuted }}>
                    沒有補發／調整
                  </td></tr>
                )}
                {adjustments.map((j) => {
                  const row = { ...j, kind: "adjustment", key: j.adj_id };
                  return (
                    <tr key={j.adj_id}>
                      {paidCell(row)}
                      <td style={{ ...tdStyle, fontFamily: "monospace" }}>{j.member_id}</td>
                      <td style={{ ...tdStyle, color: COLORS.textPrimary }}>{j.name || "—"}</td>
                      <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600,
                                   color: j.amount < 0 ? COLORS.danger : COLORS.textPrimary }}>
                        {fmt(j.amount)}
                      </td>
                      <td style={{ ...tdStyle, whiteSpace: "normal", minWidth: 180 }}>{j.reason}</td>
                      <td style={tdStyle}>{j.original_week || "—"}</td>
                      <td style={{ ...tdStyle, fontSize: 12, color: COLORS.textMuted }}>
                        {j.created_by}<br />{fmtTime(j.created_at)}
                      </td>
                      {showPaid && (
                        <td style={{ ...tdStyle, textAlign: "right" }}>
                          {!j.paid && !readOnly && (
                            <Button size="sm" onClick={() => onDeleteAdj(j)} style={{
                              ...cancelBtnStyle, padding: "3px 10px", fontSize: 12 }}>
                              刪除
                            </Button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </div>
      )}
    </>
  );
}

// ==================== 補發 / 調整 Modal ====================

function AdjustmentModal({ open, week, weeks, onClose, onSaved }) {
  const empty = { member_id: "", name: "", amount: "", reason: "", original_week: "" };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => { if (open) { setForm(empty); setErr(null); } }, [open]); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const r = await axios.post(`${API}/weeks/${week}/adjustments`, form);
      if (r.data.code !== 1) throw new Error(r.data.msg);
      onSaved(r.data.data.record);
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, props = {}) => (
    <FormGroup>
      <Label style={labelStyle}>{label}</Label>
      <Input style={inputStyle} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} {...props} />
    </FormGroup>
  );

  return (
    <Modal isOpen={open} toggle={onClose} centered>
      <ModalHeader toggle={onClose} style={modalHeaderStyle}>
        <i className="fa fa-square-plus mr-2" style={{ color: COLORS.accent }} />
        新增補發／調整 · {week}
      </ModalHeader>
      <ModalBody style={modalBodyStyle}>
        {err && <Alert color="danger" style={{ marginBottom: 12 }}>{err}</Alert>}
        <Row>
          <Col xs={12} sm={6}>{field("member_id", "遊戲 ID", { placeholder: "XXXX-XXXX" })}</Col>
          <Col xs={12} sm={6}>{field("name", "名稱（可不填）")}</Col>
          <Col xs={12} sm={6}>{field("amount", "金額（扣回用負數）", { type: "number", step: "0.01" })}</Col>
          <Col xs={12} sm={6}>
            <FormGroup>
              <Label style={labelStyle}>原本週期（可不填）</Label>
              <Input type="select" style={inputStyle} value={form.original_week}
                     onChange={(e) => setForm({ ...form, original_week: e.target.value })}>
                <option value="">—</option>
                {weeks.map((w) => <option key={w} value={w}>{w}</option>)}
              </Input>
            </FormGroup>
          </Col>
          <Col xs={12}>{field("reason", "原因", { placeholder: "例：20260907 漏算 MTT 補發" })}</Col>
        </Row>
      </ModalBody>
      <ModalFooter style={modalFooterStyle}>
        <Button onClick={onClose} style={cancelBtnStyle}>取消</Button>
        <Button onClick={save} disabled={saving} style={saveBtnStyle(saving)}>
          {saving && <TinyBusy light />} 新增
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ==================== Tab：本週發放 ====================

function WeekTab({ week, weekOptions, onChanged }) {
  const [data, setData] = useState(null);          // {record, tier_version, has_data}
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [adjOpen, setAdjOpen] = useState(false);
  const [err, setErr] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    if (!week) return;
    setLoading(true);
    setErr(null);
    try {
      const r = await axios.get(`${API}/weeks/${week}`);
      if (r.data.code !== 1) throw new Error(r.data.msg);
      setData(r.data.data);
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [week]);

  useEffect(() => { setPreview(null); setNotice(null); load(); }, [load]);

  const rec = data && data.record;
  const imported = rec && rec.source === "sheet_import";
  const payable = rowsOf(rec).filter((r) => Number(r.amount) !== 0);
  const paidCount = payable.filter((r) => r.paid).length;

  const updateRecord = (record, msg) => {
    setData((d) => ({ ...d, record }));
    if (msg) setNotice(msg);
    onChanged();
  };

  const doPreview = async () => {
    setPreviewing(true);
    setErr(null);
    setNotice(null);
    try {
      const r = await axios.post(`${API}/weeks/${week}/preview`);
      if (r.data.code !== 1) throw new Error(r.data.msg);
      setPreview(r.data.data);
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setPreviewing(false);
    }
  };

  const doSave = async () => {
    setSaving(true);
    setErr(null);
    try {
      const r = await axios.post(`${API}/weeks/${week}/run`, { fingerprint: preview.fingerprint });
      if (r.data.code !== 1) {
        if (r.data.error === "stale") setPreview(null);
        throw new Error(r.data.msg);
      }
      setPreview(null);
      updateRecord(r.data.data.record, "已儲存");
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const togglePaid = async (row, all = false) => {
    const key = all ? "all" : `${row.kind}:${row.key}`;
    setBusyKey(key);
    setErr(null);
    try {
      const body = all
        ? { items: [{ kind: "all" }], paid: true }
        : { items: [{ kind: row.kind, key: row.key }], paid: !row.paid };
      const r = await axios.post(`${API}/weeks/${week}/paid`, body);
      if (r.data.code !== 1) throw new Error(r.data.msg);
      updateRecord(r.data.data.record);
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setBusyKey(null);
    }
  };

  const deleteAdj = async (j) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`刪除 ${j.member_id} ${fmt(j.amount)}（${j.reason}）？`)) return;
    setErr(null);
    try {
      const r = await axios.delete(`${API}/weeks/${week}/adjustments/${j.adj_id}`);
      if (r.data.code !== 1) throw new Error(r.data.msg);
      updateRecord(r.data.data.record, "已刪除");
    } catch (e) {
      setErr(apiError(e));
    }
  };

  if (!data && loading) return <TinyLoading text="載入這週的發放紀錄" />;

  const shown = preview || rec;
  const totals = (shown && shown.totals) || { players: 0, agents: 0, adjustments: 0, total: 0 };

  let status = { text: "尚未計算", style: mutedBadge };
  if (imported) status = { text: "舊 Sheet 匯入", style: softBadge(COLORS.info) };
  else if (rec && payable.length && paidCount === payable.length) status = { text: "已全部發放", style: softBadge(COLORS.success) };
  else if (rec) status = { text: `發放中 ${paidCount} / ${payable.length}`, style: softBadge(COLORS.warning) };

  const canCompute = data && data.has_data && !imported;
  const diff = preview && preview.diff;
  const diffLines = diff ? ["players", "agents"].flatMap((k) => {
    const d = diff[k];
    const who = k === "players" ? "玩家" : "代理";
    return [
      ...d.added.map((x) => `新增${who} ${x.key} ${x.name || ""}：${fmt(x.amount)}`),
      ...d.changed.map((x) => `${who} ${x.key} ${x.name || ""}：${fmt(x.old)} → ${fmt(x.new)}`),
      ...d.removed.map((x) => `移除${who} ${x.key} ${x.name || ""}（原 ${fmt(x.old)}，未發放）`),
      ...d.protected.map((x) => `${who} ${x.key} ${x.name || ""} 已發放 ${fmt(x.old)}，保留不變`
        + (x.new == null ? "（重算後不在結果裡）" : `（重算為 ${fmt(x.new)}）`)),
    ];
  }) : [];

  return (
    <TinyLoadingOverlay active={loading} text="載入這週的發放紀錄">
      {err && <Alert color="danger" toggle={() => setErr(null)}>{err}</Alert>}
      {notice && <Alert color="success" toggle={() => setNotice(null)}>{notice}</Alert>}

      <TinyStatStrip items={[
        { label: "週期", icon: "fa-calendar", color: "17, 205, 239", value: week, valueStyle: { fontSize: "1rem" } },
        { label: preview ? "本週代發合計（預覽）" : "本週代發合計", icon: "fa-money-bill", color: "45, 206, 137",
          value: fmt(totals.total) },
        { label: "玩家", icon: "fa-users", color: "102, 126, 234", value: fmt(totals.players) },
        { label: "代理", icon: "fa-user-secret", color: "102, 126, 234", value: fmt(totals.agents) },
        { label: "補發／調整", icon: "fa-square-plus", color: "251, 99, 64", value: fmt(totals.adjustments) },
      ]} />

      {/* 動作列：狀態 + 計算 / 全部勾選 / 補發 */}
      <div style={{ ...sectionStyle, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <span style={status.style}>{status.text}</span>
        {rec && !imported && (
          <span style={{ fontSize: 12, color: COLORS.textMuted }}>
            {fmtTime(rec.run_at)} · {rec.run_by} · 級距 {rec.tier_version?.effective_from_week} 起
          </span>
        )}
        {imported && (
          <span style={{ fontSize: 12, color: COLORS.textMuted }}>這週是從舊 Sheet 匯入的紀錄，不能重算</span>
        )}
        {data && !data.has_data && !rec && (
          <span style={{ fontSize: 12, color: COLORS.warning }}>718000 這週的報表跑完後才能計算</span>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
          {rec && !preview && (
            <Button onClick={() => setAdjOpen(true)} style={secondaryBtn}>
              <i className="fa fa-plus mr-1" /> 補發／調整
            </Button>
          )}
          {rec && !preview && payable.length > paidCount && (
            <Button onClick={() => togglePaid(null, true)} disabled={busyKey === "all"} style={secondaryBtn}>
              {busyKey === "all" ? <TinyBusy /> : <i className="fa fa-square-check mr-1" />} 全部標為已發放
            </Button>
          )}
          {canCompute && !preview && (
            <Button onClick={doPreview} disabled={previewing} style={primaryBtn(previewing)}>
              {previewing && <TinyBusy light />} {rec ? "重新讀取並計算" : "讀取 OVB 名單並計算"}
            </Button>
          )}
        </div>
      </div>

      {/* 預覽：還沒寫進 DB */}
      {preview && (
        <div style={{ ...sectionStyle, border: `1px solid rgba(102,126,234,0.5)` }}>
          {sectionTitle("fa-eye", "預覽（尚未儲存）", (
            <div style={{ display: "flex", gap: 8 }}>
              <Button onClick={() => setPreview(null)} style={cancelBtnStyle}>取消</Button>
              <Button onClick={doSave} disabled={saving} style={primaryBtn(saving)}>
                {saving && <TinyBusy light />} 儲存
              </Button>
            </div>
          ))}
          <div style={{ fontSize: 13, color: COLORS.textSecondary, lineHeight: 1.8 }}>
            <div>
              OVB 名單 {preview.roster_count} 人，其中這週有打的 {preview.players.length} 位；
              級距 {preview.tier_version.effective_from_week} 起：
              {preview.tier_version.tiers.map((t) => ` ≥${t.min_rake} ${t.rate_pct}%`).join("、")}
            </div>
            {preview.warnings.map((w, i) => (
              <div key={i} style={{ color: COLORS.warning }}>
                <i className="fa fa-exclamation-triangle mr-1" />{w}
              </div>
            ))}
            {diff && (
              <div style={{ marginTop: 6 }}>
                <div style={{ color: COLORS.textPrimary, fontWeight: 600 }}>
                  跟目前紀錄比較{diffLines.length ? "：" : "：沒有差異"}
                </div>
                {diffLines.map((l, i) => <div key={i}>· {l}</div>)}
              </div>
            )}
          </div>
        </div>
      )}

      {shown ? (
        <>
          {!preview && (shown.flags || []).length > 0 && (
            <div style={{ ...sectionStyle, borderColor: "rgba(251,99,64,0.45)" }}>
              {sectionTitle("fa-exclamation-triangle", "可能有問題")}
              {(shown.flags || []).map((f, i) => (
                <div key={i} style={{ fontSize: 13, color: COLORS.textSecondary, marginBottom: 4 }}>
                  <span style={{ fontFamily: "monospace", color: COLORS.warning, marginRight: 8 }}>{f.member_id}</span>
                  {f.text}
                </div>
              ))}
            </div>
          )}
          <WeekTables
            rec={shown}
            flags={shown.flags}
            busyKey={busyKey}
            onToggle={(row) => togglePaid(row)}
            onDeleteAdj={deleteAdj}
            readOnly={false}
            preview={!!preview}
          />
        </>
      ) : (
        data && data.has_data && (
          <div style={{ ...sectionStyle, textAlign: "center", color: COLORS.textMuted, padding: 40 }}>
            OVB 通知名單與代理金額更新完畢後，按「讀取 OVB 名單並計算」。
          </div>
        )
      )}

      <AdjustmentModal
        open={adjOpen}
        week={week}
        weeks={weekOptions}
        onClose={() => setAdjOpen(false)}
        onSaved={(record) => { setAdjOpen(false); updateRecord(record, "已新增補發／調整"); }}
      />
    </TinyLoadingOverlay>
  );
}

// ==================== Tab：發放紀錄 ====================
//
// 兩種版型，右上角切換（Brad 2026-09-25 從清單 / 卡片 / 按人三款裡留下兩款，卡片拿掉）：
//      清單   按月分組、一週一列，點開看那週明細（會計看週合計、Apex 看發放進度）
//      按人   人員名冊 + 選中那個人的逐週時間軸（玩家來問「我哪幾週拿過多少」）
// 🚫 不要改回「人 × 週」矩陣（照 Google Sheet 搬的那種）—— 往右（週）往下（人）都會無限延伸，
//    Brad：「網頁根本沒有必要設計成像 Excel 那樣子」。兩種版型都只往下長。

const LAYOUT_KEY = "ovbRakebackHistoryLayout";
const monthOf = (w) => `${w.slice(0, 4)} 年 ${Number(w.slice(4, 6))} 月`;   // 以起日歸月（開幕週歸 12 月）
const isOff = (r) => r.calc_amount != null && Math.abs(r.calc_amount - r.amount) >= 0.02;

const textHit = (q, ...vals) => !q || vals.some((v) => (v || "").toLowerCase().includes(q));

/** records → 週清單 / 月份分組 / 人員時間軸。三種版型共用，只算一次。 */
function useHistoryModel(records) {
  return useMemo(() => {
    const weeks = (records || []).map((r) => {
      const players = (r.players || []).filter((p) => Number(p.amount));
      const agents = (r.agents || []).filter((a) => Number(a.amount));
      const adjs = (r.adjustments || []).filter((j) => Number(j.amount));
      const payable = [...players, ...agents, ...adjs];
      return {
        week: r.week,
        source: r.source,
        totals: r.totals || {},
        players, agents, adjs,
        paid: payable.filter((x) => x.paid).length,
        payable: payable.length,
        unpaid: payable.filter((x) => !x.paid).reduce((s, x) => s + Number(x.amount), 0),
        flags: flagMap(r.flags),
        flagCount: (r.flags || []).length,
      };
    });

    const months = [];
    weeks.forEach((w) => {                       // API 已經新的在前
      const label = monthOf(w.week);
      let g = months[months.length - 1];
      if (!g || g.label !== label) { g = { label, weeks: [], total: 0 }; months.push(g); }
      g.weeks.push(w);
      g.total += Number(w.totals.total || 0);
    });

    const people = {};
    const touch = (id, kind, name, nickname) => {
      const p = (people[id] = people[id]
        || { id, kind, name: "", nickname: "", total: 0, unpaid: 0, latest: "", entries: [] });
      if (kind === "agent") p.kind = "agent";
      if (name && !p.name) p.name = name;
      if (nickname && !p.nickname) p.nickname = nickname;
      return p;
    };
    weeks.forEach((w) => {
      const add = (p, e) => {
        p.entries.push({ week: w.week, ...e });
        p.total += Number(e.amount);
        if (!e.paid) p.unpaid += Number(e.amount);
        if (w.week > p.latest) p.latest = w.week;
      };
      w.players.forEach((r) => add(touch(r.member_id, "player", r.name, r.nickname),
        { amount: r.amount, paid: r.paid, calc: isOff(r) ? r.calc_amount : null, flags: w.flags[r.member_id] }));
      w.agents.forEach((r) => add(touch(r.agent_id, "agent", r.name, r.nickname),
        { amount: r.amount, paid: r.paid, agent: true, flags: w.flags[r.agent_id] }));
      w.adjs.forEach((j) => add(touch(j.member_id, "player", j.name, ""),
        { amount: j.amount, paid: j.paid, adj: j }));
      // 旗標可以落在這週沒有列的人身上（例：HBWLDS 該發沒發）
      Object.entries(w.flags).forEach(([id, texts]) => {
        const p = touch(id, "player", "", "");
        if (!p.entries.some((e) => e.week === w.week)) {
          p.entries.push({ week: w.week, amount: 0, paid: true, flags: texts, missing: true });
          if (!p.latest) p.latest = w.week;
        }
      });
    });
    Object.values(people).forEach((p) => {
      p.entries.sort((a, b) => b.week.localeCompare(a.week));
      p.count = new Set(p.entries.filter((e) => Number(e.amount)).map((e) => e.week)).size;
    });
    return { weeks, months, people: Object.values(people) };
  }, [records]);
}

/** 一週裡符合搜尋的列（玩家 / 代理 / 補發）。 */
const weekHits = (w, q) => [
  ...w.players.filter((r) => textHit(q, r.member_id, r.name, r.nickname))
    .map((r) => ({ id: r.member_id, name: r.name, nickname: r.nickname, amount: r.amount, paid: r.paid, calc: isOff(r) ? r.calc_amount : null })),
  ...w.agents.filter((r) => textHit(q, r.agent_id, r.name, r.nickname))
    .map((r) => ({ id: r.agent_id, name: r.name, nickname: r.nickname, amount: r.amount, paid: r.paid, tag: "代理" })),
  ...w.adjs.filter((j) => textHit(q, j.member_id, j.name))
    .map((j) => ({ id: j.member_id, name: j.name, amount: j.amount, paid: j.paid, tag: "補發", reason: j.reason })),
];

const ProgressBar = ({ paid, total, width = 90 }) => {
  if (!total) return <span style={{ color: COLORS.textDimmed }}>—</span>;
  const done = paid === total;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <span style={{ width, height: 6, borderRadius: 3, background: "rgba(255,255,255,0.08)", overflow: "hidden" }}>
        <span style={{ display: "block", height: "100%", width: `${(paid / total) * 100}%`,
                       background: done ? COLORS.success : COLORS.warning }} />
      </span>
      <span style={{ fontSize: 12, color: done ? COLORS.success : COLORS.warning }}>{paid}/{total}</span>
    </span>
  );
};

/** 一列人名＋金額（清單展開、卡片搜尋結果共用）。 */
const EntryLine = ({ e, flags }) => {
  const warn = !e.paid || e.calc != null;
  const tips = [
    ...(!e.paid ? ["未發放"] : []),
    ...(e.calc != null ? [`照級距應為 ${fmt(e.calc)}（差 ${fmt(e.calc - e.amount)}）`] : []),
    ...(e.reason ? [`原因：${e.reason}`] : []),
  ];
  const amount = <span style={{ color: warn ? COLORS.warning : COLORS.textPrimary, fontWeight: 600 }}>{fmt(e.amount)}</span>;
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 8, padding: "4px 0",
                  borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
      {e.tag && <span style={{ ...softBadge(e.tag === "代理" ? COLORS.info : COLORS.warning), padding: "1px 6px" }}>{e.tag}</span>}
      <span style={{ color: COLORS.textPrimary }}>{e.name || e.nickname || "—"}</span>
      {e.nickname && e.name && <span style={{ fontSize: 12, color: COLORS.textMuted }}>{e.nickname}</span>}
      <span style={{ fontFamily: "monospace", fontSize: 12, color: COLORS.textDimmed }}>{e.id}</span>
      <FlagIcon texts={flags} />
      <span style={{ marginLeft: "auto" }}>
        {tips.length ? <TinyTooltip content={<div>{tips.map((t, i) => <div key={i}>{t}</div>)}</div>}>{amount}</TinyTooltip> : amount}
      </span>
    </div>
  );
};

const weekEntries = (w) => [
  ...w.agents.map((r) => ({ id: r.agent_id, name: r.name, nickname: r.nickname, amount: r.amount, paid: r.paid, tag: "代理" })),
  ...w.players.map((r) => ({ id: r.member_id, name: r.name, nickname: r.nickname, amount: r.amount, paid: r.paid, calc: isOff(r) ? r.calc_amount : null })),
  ...w.adjs.map((j) => ({ id: j.member_id, name: j.name, amount: j.amount, paid: j.paid, tag: "補發", reason: j.reason })),
];

const emptyBox = (text) => (
  <div style={{ ...sectionStyle, textAlign: "center", color: COLORS.textMuted, padding: 40 }}>{text}</div>
);

// ---------- A. 清單 ----------

function HistoryList({ model, q, onPickWeek }) {
  const [open, setOpen] = useState({});
  const months = model.months
    .map((g) => ({ ...g, weeks: q ? g.weeks.filter((w) => weekHits(w, q).length) : g.weeks }))
    .filter((g) => g.weeks.length);
  if (!months.length) return emptyBox("沒有符合的紀錄");

  const rightTd = { ...tdStyle, textAlign: "right" };
  return (
    <div style={{ ...sectionStyle, padding: 0, overflow: "hidden" }}>
      <div style={{ overflowX: "auto" }}>
        <Table size="sm" style={{ color: COLORS.textSecondary, marginBottom: 0, minWidth: 860 }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, paddingLeft: 16 }}>週期</th>
              <th style={{ ...thStyle, textAlign: "right" }}>玩家</th>
              <th style={{ ...thStyle, textAlign: "right" }}>代理</th>
              <th style={{ ...thStyle, textAlign: "right" }}>補發／調整</th>
              <th style={{ ...thStyle, textAlign: "right" }}>合計</th>
              <th style={thStyle}>發放進度</th>
              <th style={thStyle} />
            </tr>
          </thead>
          <tbody>
            {months.map((g) => (
              <React.Fragment key={g.label}>
                <tr>
                  <td colSpan={4} style={{ ...tdStyle, paddingLeft: 16, background: COLORS.headerBg,
                                           color: COLORS.textPrimary, fontWeight: 600 }}>
                    {g.label}
                    <span style={{ marginLeft: 10, fontSize: 12, fontWeight: 400, color: COLORS.textMuted }}>{g.weeks.length} 週</span>
                  </td>
                  <td style={{ ...rightTd, background: COLORS.headerBg, color: COLORS.success, fontWeight: 600 }}>{fmt(g.total)}</td>
                  <td colSpan={2} style={{ ...tdStyle, background: COLORS.headerBg }} />
                </tr>
                {g.weeks.map((w) => {
                  const expanded = !!q || !!open[w.week];
                  return (
                    <React.Fragment key={w.week}>
                      <tr style={{ cursor: "pointer" }} onClick={() => setOpen({ ...open, [w.week]: !open[w.week] })}>
                        <td style={{ ...tdStyle, paddingLeft: 16 }}>
                          <i className={`fa fa-chevron-${expanded ? "down" : "right"}`}
                             style={{ width: 14, fontSize: 11, color: COLORS.textMuted, marginRight: 6 }} />
                          <span style={{ color: COLORS.textPrimary, fontWeight: 600 }}>{shortWeek(w.week)}</span>
                          {w.source === "sheet_import" && <span style={{ ...mutedBadge, marginLeft: 8, padding: "1px 6px" }}>匯入</span>}
                          {w.flagCount > 0 && <span style={{ ...softBadge(COLORS.warning), marginLeft: 6, padding: "1px 6px" }}>⚠ {w.flagCount}</span>}
                        </td>
                        <td style={rightTd}>
                          <span style={{ color: COLORS.textMuted, fontSize: 12, marginRight: 6 }}>{w.players.length} 人</span>
                          {fmt(w.totals.players)}
                        </td>
                        <td style={rightTd}>{w.agents.length ? fmt(w.totals.agents) : "—"}</td>
                        <td style={rightTd}>{w.adjs.length ? fmt(w.totals.adjustments) : "—"}</td>
                        <td style={{ ...rightTd, color: COLORS.textPrimary, fontWeight: 700 }}>{fmt(w.totals.total)}</td>
                        <td style={tdStyle}><ProgressBar paid={w.paid} total={w.payable} /></td>
                        <td style={{ ...rightTd, paddingRight: 16 }}>
                          <Button size="sm" style={{ ...secondaryBtn, padding: "3px 12px", fontSize: 12 }}
                                  onClick={(ev) => { ev.stopPropagation(); onPickWeek(w.week); }}>
                            開啟這週
                          </Button>
                        </td>
                      </tr>
                      {expanded && (
                        <tr>
                          <td colSpan={7} style={{ ...tdStyle, whiteSpace: "normal", background: "rgba(255,255,255,0.02)",
                                                   padding: "6px 16px 10px 40px" }}>
                            {(q ? weekHits(w, q) : weekEntries(w)).map((e, i) => (
                              <EntryLine key={i} e={e} flags={w.flags[e.id]} />
                            ))}
                            {Object.entries(w.flags).filter(([id]) => !weekEntries(w).some((e) => e.id === id))
                              .map(([id, texts]) => (
                                <div key={id} style={{ fontSize: 13, color: COLORS.warning, padding: "4px 0" }}>
                                  <i className="fa fa-exclamation-triangle mr-1" />
                                  <span style={{ fontFamily: "monospace", marginRight: 6 }}>{id}</span>{texts.join("；")}
                                </div>
                              ))}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

// ---------- B. 按人 ----------

function HistoryPeople({ model, q, onPickWeek }) {
  const [sort, setSort] = useState("latest");
  const [sel, setSel] = useState(null);
  const list = model.people
    .filter((p) => textHit(q, p.id, p.name, p.nickname))
    .sort((a, b) => {
      if (sort === "total") return b.total - a.total;
      if (sort === "name") return (a.name || a.nickname || a.id).localeCompare(b.name || b.nickname || b.id, "zh-Hant");
      return b.latest.localeCompare(a.latest) || b.total - a.total;
    });
  if (!list.length) return emptyBox("沒有符合的人");
  const p = list.find((x) => x.id === sel) || list[0];

  return (
    <Row>
      <Col xs={12} lg={5} xl={4}>
        <div style={{ ...sectionStyle, padding: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px",
                        borderBottom: `1px solid ${COLORS.border}` }}>
            <span style={{ fontSize: 13, color: COLORS.textSecondary }}>{list.length} 人</span>
            <Input type="select" bsSize="sm" value={sort} onChange={(e) => setSort(e.target.value)}
                   style={{ ...inputStyle, width: 140, marginLeft: "auto" }}>
              <option value="latest">最近發放</option>
              <option value="total">累計金額</option>
              <option value="name">名稱</option>
            </Input>
          </div>
          <div style={{ maxHeight: "65vh", overflowY: "auto" }}>
            {list.map((x) => {
              const active = x.id === p.id;
              return (
                <button key={x.id} type="button" onClick={() => setSel(x.id)} style={{
                  display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left",
                  padding: "9px 14px", border: "none", borderBottom: "1px solid rgba(255,255,255,0.04)",
                  background: active ? "rgba(102,126,234,0.14)" : "transparent", cursor: "pointer",
                }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ color: COLORS.textPrimary, fontWeight: active ? 600 : 400 }}>
                      {x.name || x.nickname || "—"}
                      {x.kind === "agent" && <span style={{ ...softBadge(COLORS.info), marginLeft: 6, padding: "0 6px" }}>代理</span>}
                      {x.unpaid > 0 && <span style={{ ...softBadge(COLORS.warning), marginLeft: 6, padding: "0 6px" }}>未發放</span>}
                    </div>
                    <div style={{ fontSize: 12, color: COLORS.textMuted }}>
                      {x.nickname && x.name ? `${x.nickname} · ` : ""}<span style={{ fontFamily: "monospace" }}>{x.id}</span>
                    </div>
                  </div>
                  <div style={{ marginLeft: "auto", textAlign: "right", whiteSpace: "nowrap" }}>
                    <div style={{ color: COLORS.textPrimary }}>{fmt(x.total)}</div>
                    <div style={{ fontSize: 12, color: COLORS.textMuted }}>{x.count} 次 · {x.latest ? shortWeek(x.latest) : "—"}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </Col>

      <Col xs={12} lg={7} xl={8}>
        <div style={sectionStyle}>
          <div style={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
            <span style={{ color: COLORS.textPrimary, fontWeight: 600, fontSize: 18 }}>{p.name || p.nickname || "—"}</span>
            {p.kind === "agent" && <span style={{ ...softBadge(COLORS.info), padding: "1px 8px" }}>代理</span>}
            <span style={{ color: COLORS.textMuted }}>{p.nickname && p.name ? p.nickname : ""}</span>
            <span style={{ fontFamily: "monospace", color: COLORS.textDimmed }}>{p.id}</span>
          </div>
          <TinyStatStrip style={{ marginBottom: 12 }} items={[
            { label: "累計", icon: "fa-money-bill", color: "45, 206, 137", value: fmt(p.total) },
            { label: "領過幾週", icon: "fa-calendar", color: "102, 126, 234", value: p.count },
            { label: "最近一次", icon: "fa-clock", color: "17, 205, 239",
              value: p.latest ? shortWeek(p.latest) : "—", valueStyle: { fontSize: "1rem" } },
            ...(p.unpaid > 0 ? [{ label: "未發放", icon: "fa-exclamation-triangle", color: "251, 99, 64", value: fmt(p.unpaid) }] : []),
          ]} />
          <Table size="sm" style={{ color: COLORS.textSecondary, marginBottom: 0 }}>
            <thead>
              <tr>
                <th style={thStyle}>週期</th>
                <th style={{ ...thStyle, textAlign: "right" }}>金額</th>
                <th style={thStyle}>狀態</th>
                <th style={thStyle}>備註</th>
              </tr>
            </thead>
            <tbody>
              {p.entries.map((e, i) => {
                const notes = [
                  ...(e.agent ? ["代理分潤"] : []),
                  ...(e.adj ? [`補發／調整：${e.adj.reason}${e.adj.original_week ? `（原 ${e.adj.original_week}）` : ""}`] : []),
                  ...(e.calc != null ? [`照級距應為 ${fmt(e.calc)}`] : []),
                ];
                return (
                  <tr key={`${e.week}-${i}`}>
                    <td style={tdStyle}>
                      <a href="#week" onClick={(ev) => { ev.preventDefault(); onPickWeek(e.week); }}
                         style={{ color: COLORS.textPrimary }}>{shortWeek(e.week)}</a>
                      <span style={{ fontSize: 11, color: COLORS.textDimmed, marginLeft: 6 }}>{e.week.slice(0, 4)}</span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600,
                                 color: e.missing ? COLORS.textDimmed : e.calc != null || !e.paid ? COLORS.warning : COLORS.textPrimary }}>
                      {e.missing ? "—" : fmt(e.amount)}
                    </td>
                    <td style={tdStyle}>
                      {e.missing ? null : e.paid
                        ? <span style={{ ...softBadge(COLORS.success), padding: "1px 6px" }}>已發放</span>
                        : <span style={{ ...softBadge(COLORS.warning), padding: "1px 6px" }}>未發放</span>}
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: "normal", fontSize: 13 }}>
                      {notes.join("；")}
                      {e.flags && (
                        <div style={{ color: COLORS.warning }}>
                          <i className="fa fa-exclamation-triangle mr-1" />{e.flags.join("；")}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      </Col>
    </Row>
  );
}

// ---------- 外殼：摘要 + 搜尋 + 版型切換 ----------

function HistoryTab({ records, loading, onPickWeek }) {
  const model = useHistoryModel(records);
  const [layout, setLayout] = useState(() => {
    // 評估期存過「卡片」的瀏覽器，退回清單
    try { return localStorage.getItem(LAYOUT_KEY) === "people" ? "people" : "list"; } catch (e) { return "list"; }
  });
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();

  const pickLayout = (k) => {
    setLayout(k);
    try { localStorage.setItem(LAYOUT_KEY, k); } catch (e) { /* 私密視窗等情況存不了，不影響 */ }
  };

  if (loading && !records) return <TinyLoading text="載入發放紀錄" />;
  if (!records || records.length === 0) return emptyBox("還沒有任何發放紀錄");

  const total = model.weeks.reduce((s, w) => s + Number(w.totals.total || 0), 0);
  const unpaid = model.weeks.reduce((s, w) => s + w.unpaid, 0);
  const flags = model.weeks.reduce((s, w) => s + w.flagCount, 0);

  return (
    <TinyLoadingOverlay active={loading} text="更新發放紀錄">
      <TinyStatStrip items={[
        { label: "累計代發", icon: "fa-money-bill", color: "45, 206, 137", value: fmt(total) },
        { label: "週數", icon: "fa-calendar", color: "102, 126, 234", value: model.weeks.length },
        { label: "領過退水的人", icon: "fa-users", color: "17, 205, 239", value: model.people.filter((p) => p.count).length },
        { label: "未發放", icon: "fa-hourglass-half", color: "251, 99, 64", value: fmt(unpaid) },
        { label: "可能有問題", icon: "fa-exclamation-triangle", color: "251, 99, 64", value: flags },
      ]} />

      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 14 }}>
        <div style={{ position: "relative", flex: "1 1 260px", maxWidth: 380 }}>
          <i className="fa fa-search" style={{ position: "absolute", left: 11, top: 11, color: COLORS.textDimmed, fontSize: 13 }} />
          <Input value={query} onChange={(e) => setQuery(e.target.value)}
                 placeholder="搜尋遊戲 ID／遊戲暱稱／OVB 提供名稱"
                 style={{ ...inputStyle, paddingLeft: 32 }} />
        </div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, color: COLORS.textMuted }}>版型</span>
          <TinyPageTabs
            look="pill"
            items={[
              { key: "list", label: "清單", icon: "fa fa-list" },
              { key: "people", label: "按人", icon: "fa fa-user" },
            ]}
            active={layout}
            onChange={pickLayout}
          />
        </div>
      </div>

      {layout === "list" && <HistoryList model={model} q={q} onPickWeek={onPickWeek} />}
      {layout === "people" && <HistoryPeople model={model} q={q} onPickWeek={onPickWeek} />}
    </TinyLoadingOverlay>
  );
}

// ==================== Tab：級距設定 ====================

function TiersTab() {
  const [data, setData] = useState(null);   // {versions, current, weeks}
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(null);   // {eff, rows:[{min_rake, rate_pct}], note}
  const [confirm, setConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/tiers`);
      if (r.data.code !== 1) throw new Error(r.data.msg);
      setData(r.data.data);
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const versions = (data && data.versions) || [];
  const currentId = data && data.current && data.current.id;
  const current = versions.find((v) => v._id === currentId);

  // 生效週選項：最新有資料那週之後的 8 週（通常就選下一週）+ 既有週期
  const effOptions = useMemo(() => {
    const weeks = (data && data.weeks) || [];
    if (!weeks.length) return [];
    const future = [];
    let w = weeks[0];
    for (let i = 0; i < 8; i += 1) { w = weekAfter(w); future.push(w); }
    return [...future.reverse(), ...weeks];
  }, [data]);

  const startEdit = () => {
    const weeks = (data && data.weeks) || [];
    setForm({
      eff: weeks.length ? weekAfter(weeks[0]) : "",
      rows: ((current && current.tiers) || [{ min_rake: 500, rate_pct: 30 }]).map((t) => ({
        min_rake: String(t.min_rake), rate_pct: String(t.rate_pct) })),
      note: "",
    });
    setErr(null);
    setNotice(null);
  };

  const payload = () => ({
    effective_from_week: form.eff,
    tiers: form.rows.map((r) => ({ min_rake: Number(r.min_rake), rate_pct: Number(r.rate_pct) })),
    note: form.note,
  });

  const check = async () => {
    setSaving(true);
    setErr(null);
    try {
      const r = await axios.post(`${API}/tiers`, { ...payload(), dry_run: true });
      if (r.data.code !== 1) throw new Error(r.data.msg);
      setConfirm({ affected: r.data.data.affected_weeks });
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const r = await axios.post(`${API}/tiers`, payload());
      if (r.data.code !== 1) throw new Error(r.data.msg);
      setConfirm(null);
      setForm(null);
      setNotice(`已新增級距版本，${r.data.data.saved.effective_from_week} 起生效`);
      load();
    } catch (e) {
      setErr(apiError(e));
      setConfirm(null);
    } finally {
      setSaving(false);
    }
  };

  if (!data && loading) return <TinyLoading text="載入級距" />;

  const tierText = (tiers) => (tiers || []).map((t) => `≥${Number(t.min_rake).toLocaleString()} ${t.rate_pct}%`).join("、");

  return (
    <>
      {err && <Alert color="danger" toggle={() => setErr(null)}>{err}</Alert>}
      {notice && <Alert color="success" toggle={() => setNotice(null)}>{notice}</Alert>}

      <Row>
        <Col xs={12} xl={5}>
          <div style={sectionStyle}>
            {sectionTitle("fa-sliders", `目前適用的級距${data && data.current ? `（${data.current.week}）` : ""}`,
              !form && <Button onClick={startEdit} style={secondaryBtn}><i className="fa fa-plus mr-1" /> 新增級距版本</Button>)}
            {current ? (
              <Table size="sm" style={{ color: COLORS.textSecondary, marginBottom: 0 }}>
                <thead>
                  <tr>
                    <th style={thStyle}>水量（含 MTT）</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>退水</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={tdStyle}>未滿 {Number(current.tiers[0].min_rake).toLocaleString()}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>0%</td>
                  </tr>
                  {current.tiers.map((t, i) => (
                    <tr key={i}>
                      <td style={tdStyle}>
                        {Number(t.min_rake).toLocaleString()} 以上
                        {current.tiers[i + 1] ? `、未滿 ${Number(current.tiers[i + 1].min_rake).toLocaleString()}` : ""}
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right", color: COLORS.textPrimary, fontWeight: 600 }}>{t.rate_pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <div style={{ color: COLORS.warning, fontSize: 13 }}>還沒有適用的級距，請新增一個版本</div>
            )}
          </div>

          {form && (
            <div style={{ ...sectionStyle, border: "1px solid rgba(102,126,234,0.5)" }}>
              {sectionTitle("fa-plus", "新增級距版本")}
              <FormGroup>
                <Label style={labelStyle}>從哪一週開始生效</Label>
                <Input type="select" style={inputStyle} value={form.eff}
                       onChange={(e) => setForm({ ...form, eff: e.target.value })}>
                  {effOptions.map((w) => <option key={w} value={w}>{w}</option>)}
                </Input>
              </FormGroup>
              <Label style={labelStyle}>級距（未滿第一級 = 0%）</Label>
              {form.rows.map((r, i) => (
                <Row key={i} form style={{ marginBottom: 8, alignItems: "center" }}>
                  <Col xs={5}>
                    <Input type="number" style={inputStyle} value={r.min_rake} placeholder="水量門檻"
                           onChange={(e) => {
                             const rows = [...form.rows]; rows[i] = { ...r, min_rake: e.target.value };
                             setForm({ ...form, rows });
                           }} />
                  </Col>
                  <Col xs={1} style={{ color: COLORS.textMuted, padding: 0, textAlign: "center" }}>以上</Col>
                  <Col xs={4}>
                    <Input type="number" style={inputStyle} value={r.rate_pct} placeholder="%"
                           onChange={(e) => {
                             const rows = [...form.rows]; rows[i] = { ...r, rate_pct: e.target.value };
                             setForm({ ...form, rows });
                           }} />
                  </Col>
                  <Col xs={2} style={{ textAlign: "right" }}>
                    {form.rows.length > 1 && (
                      <Button size="sm" style={{ ...cancelBtnStyle, padding: "4px 10px" }}
                              onClick={() => setForm({ ...form, rows: form.rows.filter((_, j) => j !== i) })}>
                        <i className="fa fa-times" />
                      </Button>
                    )}
                  </Col>
                </Row>
              ))}
              <Button size="sm" style={{ ...secondaryBtn, padding: "4px 12px", marginBottom: 12 }}
                      onClick={() => setForm({ ...form, rows: [...form.rows, { min_rake: "", rate_pct: "" }] })}>
                <i className="fa fa-plus mr-1" /> 加一級
              </Button>
              <FormGroup>
                <Label style={labelStyle}>備註</Label>
                <Input style={inputStyle} value={form.note} placeholder="例：OVB 2026-10 通知調整"
                       onChange={(e) => setForm({ ...form, note: e.target.value })} />
              </FormGroup>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <Button onClick={() => setForm(null)} style={cancelBtnStyle}>取消</Button>
                <Button onClick={check} disabled={saving} style={primaryBtn(saving)}>
                  {saving && <TinyBusy light />} 儲存
                </Button>
              </div>
            </div>
          )}
        </Col>

        <Col xs={12} xl={7}>
          <div style={sectionStyle}>
            {sectionTitle("fa-history", "級距版本")}
            <div style={{ overflowX: "auto" }}>
              <Table size="sm" style={{ color: COLORS.textSecondary, marginBottom: 0, minWidth: 560 }}>
                <thead>
                  <tr>
                    <th style={thStyle}>生效週</th>
                    <th style={thStyle}>級距</th>
                    <th style={thStyle}>備註</th>
                    <th style={thStyle}>建立</th>
                  </tr>
                </thead>
                <tbody>
                  {versions.map((v) => (
                    <tr key={v._id}>
                      <td style={tdStyle}>
                        {v.effective_from_week}
                        {v._id === currentId && <span style={{ ...softBadge(COLORS.success), marginLeft: 8 }}>目前適用</span>}
                      </td>
                      <td style={tdStyle}>{tierText(v.tiers)}</td>
                      <td style={{ ...tdStyle, whiteSpace: "normal" }}>{v.note || "—"}</td>
                      <td style={{ ...tdStyle, fontSize: 12, color: COLORS.textMuted }}>
                        {v.created_by}<br />{fmtTime(v.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        </Col>
      </Row>

      <Modal isOpen={!!confirm} toggle={() => setConfirm(null)} centered>
        <ModalHeader toggle={() => setConfirm(null)} style={modalHeaderStyle}>確認新增級距版本</ModalHeader>
        <ModalBody style={modalBodyStyle}>
          {form && (
            <div style={{ fontSize: 14, lineHeight: 1.9 }}>
              <div><span style={{ color: COLORS.textMuted }}>生效週</span>　{form.eff} 起</div>
              <div><span style={{ color: COLORS.textMuted }}>級距</span>　{tierText(payload().tiers)}</div>
              {confirm && confirm.affected.length > 0 && (
                <div style={{ marginTop: 10, color: COLORS.warning }}>
                  以下已算過的週，之後若重新計算會改用新級距（已發放的金額不會變）：
                  <div style={{ color: COLORS.textSecondary }}>{confirm.affected.join("、")}</div>
                </div>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter style={modalFooterStyle}>
          <Button onClick={() => setConfirm(null)} style={cancelBtnStyle}>取消</Button>
          <Button onClick={save} disabled={saving} style={saveBtnStyle(saving)}>
            {saving && <TinyBusy light />} 確認新增
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

// ==================== 頁面 ====================

export default function TinyOvbRakeback() {
  const [tab, setTab] = useState("week");
  const [weeks, setWeeks] = useState(null);        // week_summaries
  const [week, setWeek] = useState("");
  const [err, setErr] = useState(null);
  const [history, setHistory] = useState(null);
  const [historyStale, setHistoryStale] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/weeks`)
      .then((r) => {
        if (r.data.code !== 1) throw new Error(r.data.msg);
        const list = r.data.data.weeks;
        setWeeks(list);
        if (list.length) setWeek(list[0].week);
      })
      .catch((e) => setErr(apiError(e)));
  }, []);

  useEffect(() => {
    if (tab !== "history" || !historyStale) return;
    setHistoryLoading(true);
    axios.get(`${API}/history`)
      .then((r) => {
        if (r.data.code !== 1) throw new Error(r.data.msg);
        setHistory(r.data.data.records);
        setHistoryStale(false);
      })
      .catch((e) => setErr(apiError(e)))
      .finally(() => setHistoryLoading(false));
  }, [tab, historyStale]);

  const weekOptions = useMemo(() => (weeks || []).map((w) => w.week), [weeks]);

  return (
    <div className="content">
      <TinyPageHeader
        title="OVB 代發退水"
        description="依 OVB 的退水名單與我方級距，計算每週代發給玩家與代理的金額，並記錄發放狀態。"
        reserveScope
        scope={tab === "week" && weekOptions.length > 0 ? (
          <TinyWeekSelector label="" options={weekOptions} value={week} onChange={setWeek} showMeta={false} />
        ) : null}
        tabs={{
          items: [
            { key: "week", label: "本週發放", icon: "fa fa-calendar-check" },
            { key: "history", label: "發放紀錄", icon: "fa fa-table" },
            { key: "tiers", label: "級距設定", icon: "fa fa-sliders" },
          ],
          active: tab,
          onChange: setTab,
        }}
      />

      {err && <Alert color="danger" toggle={() => setErr(null)}>{err}</Alert>}

      {tab === "week" && (
        weeks === null
          ? <TinyLoading text="載入週期" />
          : week
            ? <WeekTab week={week} weekOptions={weekOptions} onChanged={() => setHistoryStale(true)} />
            : <div style={{ ...sectionStyle, textAlign: "center", color: COLORS.textMuted, padding: 40 }}>還沒有任何週期的資料</div>
      )}
      {tab === "history" && (
        <HistoryTab
          records={history}
          loading={historyLoading}
          onPickWeek={(w) => { if (weekOptions.includes(w)) { setWeek(w); setTab("week"); } }}
        />
      )}
      {tab === "tiers" && <TiersTab />}
    </div>
  );
}
